import {
    CoachingType,
    FocusForPerformerCoachableMetric,
} from '../../../coaching-base/general';
import {
    CellNode,
    Column,
    Row,
    RowNode,
    Sort,
    SortOrder,
} from '../grid/experimental.grid';
import {
    CellContext,
    ColumnID,
    defaultSortDefs,
    MetricGrid,
    RowContext,
} from '../model/focus-selector-metric-selection.grid.model';
import { FocusSelectorMetricModel } from '../model/focus-selector-metric.model';
import { TemplateID } from '../model/focus-selector.grid.model';

export function makeColumns(): Column[] {
    // TODO: verify if height is needed
    return [
        {
            id: ColumnID.METRIC,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 200,
        },
        {
            id: ColumnID.OA,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 100,
        },
        {
            id: ColumnID.ON_SCORECARD,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 150,
        },
        {
            id: ColumnID.ACTUAL,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 100,
        },
        {
            id: ColumnID.TARGET,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 100,
        },
        {
            id: ColumnID.PERCENT_TO_TARGET,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 150,
        },
        {
            id: ColumnID.SUPERGROUP_PERCENT_TO_TARGET,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 120,
        },
    ];
}

const coachingTypeWeightMap = {
    [CoachingType.UNCOACHED]: 1,
    [CoachingType.COACHED]: 2,
    [CoachingType.NON_OPPORTUNITY]: 3,
    // others would have been filtered out
};

export function compare(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    const metricA: FocusSelectorMetricModel = rowA.context.ctx.metric;
    const metricB: FocusSelectorMetricModel = rowB.context.ctx.metric;
    let valA: number;
    let valB: number;
    const delta = def.order === SortOrder.ASC ? 1 : -1;

    switch (def.columnId) {
        case ColumnID.OA:
            valA =
                metricA.coachTypeId == null
                    ? delta * 999
                    : coachingTypeWeightMap[metricA.coachTypeId];
            valB =
                metricB.coachTypeId == null
                    ? delta * 999
                    : coachingTypeWeightMap[metricB.coachTypeId];
            break;
        case ColumnID.ON_SCORECARD:
            valA = metricA.isInScorecard === true ? 999 : 1;
            valB = metricB.isInScorecard === true ? 999 : 1;
            break;
        case ColumnID.PERCENT_TO_TARGET:
            valA =
                metricA.percentToTarget != null
                    ? metricA.percentToTarget
                    : Number.MIN_SAFE_INTEGER;
            valB =
                metricB.percentToTarget != null
                    ? metricB.percentToTarget
                    : Number.MIN_SAFE_INTEGER;
            break;
        default:
            return Number.MIN_SAFE_INTEGER;
    }

    return def.order === SortOrder.ASC ? valA - valB : valB - valA;
}

export function makeRows(
    metrics: FocusSelectorMetricModel[],
    activeMetric?: FocusSelectorMetricModel,
): MetricGrid['rows'] {
    const onlyCoachingTypes = [
        CoachingType.UNCOACHED,
        CoachingType.COACHED,
        CoachingType.NON_OPPORTUNITY,
        null,
        undefined,
    ];

    return metrics
        .filter((metric) => onlyCoachingTypes.includes(metric.coachTypeId))
        .map<Row<RowContext, CellContext>>((metric) => {
            const checked = activeMetric?.id === metric.id;
            const {
                name,
                coachTypeId,
                isInScorecard,
                actual,
                target,
                percentToTarget,
                superGroupPercentToTarget,
                targetThresholdColor,
                superGroupTargetThresholdColor,
            } = metric;

            return {
                id: metric.id.toString(),
                ctx: {
                    templateId: TemplateID.CONTENT,
                    metric,
                },
                cells: [
                    {
                        columnId: ColumnID.METRIC,
                        ctx: {
                            templateId: TemplateID.CONTENT,
                            checked,
                            name,
                        },
                    },
                    {
                        columnId: ColumnID.OA,
                        ctx: {
                            templateId: TemplateID.CONTENT,
                            checked,
                            coachTypeId,
                        },
                    },
                    {
                        columnId: ColumnID.ON_SCORECARD,
                        ctx: {
                            templateId: TemplateID.CONTENT,
                            checked,
                            isInScorecard,
                        },
                    },
                    {
                        columnId: ColumnID.ACTUAL,
                        ctx: {
                            templateId: TemplateID.CONTENT,
                            checked,
                            actual,
                        },
                    },
                    {
                        columnId: ColumnID.TARGET,
                        ctx: {
                            templateId: TemplateID.CONTENT,
                            checked,
                            target,
                        },
                    },
                    {
                        columnId: ColumnID.PERCENT_TO_TARGET,
                        ctx: {
                            templateId: TemplateID.CONTENT,
                            checked,
                            percentToTarget,
                            targetThresholdColor,
                        },
                    },
                    {
                        columnId: ColumnID.SUPERGROUP_PERCENT_TO_TARGET,
                        ctx: {
                            templateId: TemplateID.CONTENT,
                            checked,
                            superGroupPercentToTarget,
                            superGroupTargetThresholdColor,
                        },
                    },
                ],
            };
        });
}

export function makeGrid(
    coachableMetrics: FocusSelectorMetricModel[],
    activeMetric?: FocusSelectorMetricModel,
): MetricGrid {
    const columns = makeColumns();
    const rows = makeRows(coachableMetrics, activeMetric);
    return {
        columns,
        rows,
        sortDefs: defaultSortDefs,
        defaultSortDefs,
        gridRowsTop: [],
        rendererOpts: {
            columns,
            defaultRenderFn: null,
        },
    };
}

export function formatCoachableMetrics(
    coachableMetrics: FocusForPerformerCoachableMetric[],
): FocusSelectorMetricModel[] {
    return coachableMetrics.map((coachableMetric) => ({
        id: coachableMetric.metricId ?? null,
        name: coachableMetric.metricFullName ?? '',
        coachTypeId: coachableMetric.coachTypeId ?? null,
        isOppActive: coachableMetric.isOppActive ?? null,
        isInScorecard: coachableMetric.isInScorecard ?? null,
        percentToTarget:
            coachableMetric.percentToTarget != null
                ? coachableMetric.percentToTarget / 100
                : null,
        actual: coachableMetric.actual ?? null,
        target: coachableMetric.target ?? null,
        superGroupPercentToTarget:
            coachableMetric.superGroupPercentToTarget != null
                ? coachableMetric.superGroupPercentToTarget / 100
                : null,
        targetThresholdColor: coachableMetric.targetThresholdColour ?? '#000',
        superGroupTargetThresholdColor:
            coachableMetric.superGroupTargetThresholdColour ?? '#000',
        rankOrder: coachableMetric.rankOrder,
    }));
}

export function getSelectedMetricFromGrid(
    gridData: MetricGrid,
): FocusSelectorMetricModel {
    let selectedRow: FocusSelectorMetricModel = { id: null };

    for (const row of gridData.rows) {
        if (row.cells.length > 0 && row.cells[0].ctx?.checked === true) {
            selectedRow = row.ctx.metric;
        }
    }

    return selectedRow;
}
